import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaSearch } from "react-icons/fa";
import AddSupplierPopup from "../AddSupplierPopup";
import { supplierIdentityIds } from "../../utils/supplierIdentity";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import { useOriginalDocumentDialogScope } from "../originals/OriginalDocumentDialogScope";

const supplierMatchesId = (supplier, id) => supplierIdentityIds(supplier).includes(Number(id));
const normalizeLookup = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().replace(/\s+/g, " ").toLowerCase();

export default function OcrSupplierPickerModal({
  open,
  suppliers,
  selectedSupplierId,
  detectedSupplier,
  initialForm,
  onSelect,
  onCreated,
  businessUnitId,
  scopedApi,
  distributorName,
  allowCreate = true,
  manualEntry = false,
  onClose,
  loading = false,
  error,
  onRetry,
  text,
  disabled = false,
}) {
  const { t: defaultText, themeId } = useReceivingPopupUi();
  const t = text || defaultText;
  const { isSplit } = useOriginalDocumentDialogScope();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("list");

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setMode("list");
  }, [
    open,
    initialForm,
    initialForm?.name,
    initialForm?.supplier_id,
    initialForm?.tax_id,
    initialForm?.phone,
    initialForm?.email,
    initialForm?.address,
    initialForm?.contact_name,
  ]);

  const filteredSuppliers = useMemo(() => {
    const normalizedQuery = normalizeLookup(query);
    if (!normalizedQuery) return suppliers || [];
    return (suppliers || []).filter((supplier) =>
      normalizeLookup([
        supplier?.name,
        supplier?.supplier_id,
        supplier?.tax_id,
      ].filter(Boolean).join(" ")).includes(normalizedQuery)
    );
  }, [query, suppliers]);

  if (!open) return null;

  if (mode === "create") return <AddSupplierPopup isOpen requiredUsage="stock" businessUnitId={businessUnitId}
    initialValues={initialForm} requestClient={scopedApi} onClose={() => setMode("list")}
    onAdded={(supplier) => { onCreated?.(supplier); onClose?.(); }} />;

  return (
    <div className="ocrp-pickerOverlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <div className="ocrp-pickerModal" onMouseDown={(event) => event.stopPropagation()} role="dialog" aria-modal={!isSplit} aria-label={t("Choose or create supplier")}>
        <div className="ocrp-pickerHead">
          <div>
            <h3>{mode === "create" ? t("Create supplier") : t(allowCreate ? "Choose or create supplier" : "Choose supplier")}</h3>
            {manualEntry ? <p>{t("Search suppliers")}</p> : <p>
              {mode === "create"
                ? t("Review the values detected in the document before creating the supplier.")
                : allowCreate ? t("Search the supplier list or create a new supplier from the scanned document.") : t("Choose supplier")}
            </p>}
          </div>
          <button type="button" className="ocrp-btn" onClick={onClose}> {t("Close")} </button>
        </div>

        <div className="ocrp-pickerBody">
              {(detectedSupplier || allowCreate) && <div className="ocrp-pickerIntro">
                {!manualEntry && <div>
                  <strong>{detectedSupplier || t("No supplier text detected")}{detectedSupplier && distributorName && <em className="ocrp-distributorName">{distributorName}</em>}</strong>
                  <span>{t("Detected supplier from OCR")}</span>
                </div>}
                {allowCreate && <button type="button" className="ocrp-btn ocrp-btn--primary" disabled={disabled || loading || Boolean(error)} onClick={() => setMode("create")}>
                  <FaPlus />
                  <span>{t("Create supplier")}</span>
                </button>}
              </div>}

              <div className="ocrp-search">
                <FaSearch />
                <input
                  className="ocrp-input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("Search supplier, code or tax ID...")}
                />
              </div>

              <div className="ocrp-pickerList">
                {loading ? <p role="status">{t("Loading…")}</p> : error ? <p role="alert">{t(error)} <button type="button" className="ocrp-btn" onClick={onRetry}>{t("Retry")}</button></p> : filteredSuppliers.length ? (
                  filteredSuppliers.map((supplier) => (
                    <div key={supplier.id} className="ocrp-pickerRow">
                      <div>
                        <div className="ocrp-pickerRowTitle">{supplier.name}{distributorName && <em className="ocrp-distributorName">{distributorName}</em>}</div>
                        <div className="ocrp-pickerRowMeta">
                          {[supplier.supplier_id, supplier.tax_id]
                            .filter(Boolean)
                            .join(" · ") || t("No extra supplier data")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`ocrp-btn ${supplierMatchesId(supplier, selectedSupplierId) ? "ocrp-btn--success" : "ocrp-btn--info"}`}
                        disabled={disabled}
                        onClick={() => onSelect?.(supplier)}
                      >
                        {supplierMatchesId(supplier, selectedSupplierId) ? t("Selected") : t("Select")}
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="ocrp-empty">
                    <strong>{t("No suppliers found")}</strong>
                    {!manualEntry && <span className="ocrp-small">{t("Create a supplier from the scanned document.")}</span>}
                  </div>
                )}
              </div>

        </div>
      </div>
    </div>
  );
}
